import styled, {css} from 'styled-components';

import {hexToRgba} from '@edna/utils';

import {TPosition, mixins} from '../styles';
import {TPlacementX, TPlacementY} from '../types';

export const Dropdown = styled.div`
  position: relative;
`;

type THeadProps = {
  name?: string;
  readOnly?: boolean;
  disabled?: boolean;
};

export const Head = styled.div<THeadProps>`
  width: 100%;
  min-height: 100%;
  outline: none;
  ${({disabled, readOnly}: THeadProps) => {
    if (disabled) {
      return mixins.disabled;
    }

    if (readOnly) {
      return css`
        cursor: default;
      `;
    }

    return css`
      cursor: pointer;
    `;
  }}
`;

type TOverlayProps = {
  backgroundColor?: string;
  hasVerticalPaddings?: boolean;
  hasArrow?: boolean;
  placementY: TPlacementY;
  placementX: TPlacementX;
  offsetY: number;
  offsetX: number;
};

const getArrowPosition = ({placementY, placementX}: TOverlayProps) => {
  const result: TPosition = {};

  if (placementY === 'top') {
    result.top = 'calc(100% - 6px)';
    result.transform = 'rotate(135deg)';
  }

  if (placementY === 'bottom') {
    result.bottom = 'calc(100% - 6px)';
    result.transform = 'rotate(-45deg)';
  }

  if (placementX === 'left') {
    result.left = '14px';
  }

  if (placementX === 'right') {
    result.right = '14px';
  }

  if (placementX === 'center') {
    result.left = '50%';
    result.transform = `translate(-50%, 0)${result.transform ? ` ${result.transform}` : ''}`;
  }

  return result;
};

const getPosition = ({placementY, placementX, offsetY, offsetX}: TOverlayProps) => {
  const result: TPosition = {};

  if (placementY === 'top') {
    result.bottom = offsetY ? `calc(100% + ${offsetY}px)` : '100%';
  }

  if (placementY === 'bottom') {
    result.top = offsetY ? `calc(100% + ${offsetY}px)` : '100%';
  }

  if (placementX === 'left') {
    result.left = `${offsetX}px`;
  }

  if (placementX === 'right') {
    result.right = `${offsetX}px`;
  }

  if (placementX === 'center') {
    result.left = offsetX ? `calc(50% + ${offsetX}px)` : '50%';
    result.transform = 'translate(-50%, 0)';
  }

  return result;
};

export const Overlay = styled.div<TOverlayProps>`
  ${mixins.paper};
  ${(props) => {
    const {
      theme: {
        palette,
        spacing,
        zIndex,
        animation: {duration},
        boxShadow,
      },
      backgroundColor,
      hasVerticalPaddings,
      hasArrow,
    } = props;
    const position = getPosition(props);
    const arrowPosition = getArrowPosition(props);
    const arrowColor = backgroundColor ?? palette.white;

    return css`
      ${mixins.position({position: 'absolute', ...position})}
      z-index: ${zIndex.dropdown};
      padding: ${hasVerticalPaddings ? spacing.s2 : 0} 0;
      background-color: ${backgroundColor};
      transition: top ${duration}ms;
      box-shadow: ${boxShadow.dropdown};
      ${hasArrow &&
      css`
        &::before {
          content: '';
          ${mixins.position({position: 'absolute', ...arrowPosition})}
          border: 6px solid transparent;
          border-color: ${arrowColor} ${arrowColor} transparent transparent;
          box-shadow: 2px -2px 2px 0 ${hexToRgba(palette.black, 0.1)};
        }
      `}
    `;
  }}
`;
